import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { PaginatorModule } from 'primeng/paginator';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { Account } from '../../model/account/account.model';
import { UserMenuComponent } from '../../shared/user-menu/user-menu.component';
import { UserFormModalComponent } from './user-form-modal/user-form-modal.component';
import { AccountService } from '../../model/account/account.service';
import { PermissionService } from '../../permissions.service';
import { Organization } from '../../model/organization/organization.model';

import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { Region } from "../../model/region/region.model";
import { ActivatedRoute } from "@angular/router";
import { LocalizationService } from '../../shared/localization.service';

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    PaginatorModule,
    CardModule,
    SkeletonModule,
    UserMenuComponent,
    UserFormModalComponent,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit {
  @ViewChild('userFormModal') userFormModal!: UserFormModalComponent;
  accounts: Account[] = [];
  regions: Region[] = [];
  organizations: Organization[] = [];
  roles = [] as { label: string; value: string }[];
  loading = true;
  totalRecords = 0;
  pageSize = 10;
  currentPage = 1;
  currentPageReportTemplate = '';
  pageSizeOptions = [
    { label: $localize`:@@pageSize10:10`, value: 10 },
    { label: $localize`:@@pageSize25:25`, value: 25 },
    { label: $localize`:@@pageSize50:50`, value: 50 },
    { label: $localize`:@@pageSize100:100`, value: 100 },
  ];

  constructor(
    private route: ActivatedRoute,
    private accountService: AccountService,
    private confirmationService: ConfirmationService,
    private permissionService: PermissionService,
    private localizationService: LocalizationService
  ) { }

  ngOnInit(): void {
    this.route.data.subscribe(data => {

      this.accounts = data['accounts'];
      this.regions = data['regions'];
      this.organizations = data['organizations'];

      // IMPORTANT
      this.totalRecords = this.accounts.length;

      this.updateCurrentPageReport();

      this.loading = false;
    });

    this.roles = this.permissionService.dropdownRoles.map(role => ({
      label: role,
      value: role
    }));
  }

  private async loadAccounts(): Promise<void> {
    try {
      this.loading = true;
      this.accounts = await this.accountService.fetchAll();
      this.totalRecords = this.accounts.length;
      this.updateCurrentPageReport();
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  openNewAccountModal(): void {
    this.userFormModal.show();
  }

  async onSaveAccount(data: any): Promise<void> {
    try {
      if (this.userFormModal.isNewUser) {
        await this.accountService.create(data);
      } else {
        await this.accountService.update(data);
      }
      await this.loadAccounts();
    } catch (err) {
      console.error(err);
    }
  }

  editAccount(account: Account): void {
    this.userFormModal.show(account);
  }

  confirmDelete(account: Account) {
    this.confirmationService.confirm({
      target: document.body,
      message: $localize`:@@deleteUserDialogMessage:Are you sure you want to delete this user?`,
      header: $localize`:@@deleteUserDialogHeader:Delete Confirmation`,
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteAccount(account)
    });
  }

  async deleteAccount(account: Account): Promise<void> {
    if (!account.id) return;
    try {
      this.loading = true;
      await this.accountService.delete(account.id);
      await this.loadAccounts();
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  onPageChange(event: any): void {
    this.currentPage = event.first / event.rows + 1;
    this.pageSize = event.rows;
    this.updateCurrentPageReport();
  }

  onPageSizeChange(event: any): void {
    this.pageSize = event.value;
    this.currentPage = 1;
    this.updateCurrentPageReport();
  }

  updateCurrentPageReport(): void {
    if (this.totalRecords === 0) {
      this.currentPageReportTemplate =
        $localize`:@@paginationEmpty:Showing 0 to 0 of 0 entries`;
      return;
    }

    const firstRaw = (this.currentPage - 1) * this.pageSize + 1;
    const lastRaw = Math.min(
      this.currentPage * this.pageSize,
      this.totalRecords
    );

    const first = this.localizationService.localizeNumber(firstRaw);
    const last = this.localizationService.localizeNumber(lastRaw);
    const total = this.localizationService.localizeNumber(this.totalRecords);
    console.log(first, last, total);

    this.currentPageReportTemplate =
      $localize`:@@paginationReport:Showing ${first}:first: to ${last}:last: of ${total}:total: entries`;
  }
}